import Vue from 'vue';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginVue from '@bugsnag/plugin-vue';
import VueRouter from 'vue-router';
// https://github.com/declandewet/vue-meta
import VueMeta from 'vue-meta';
// Adds a loading bar at the top during page loads.
import NProgress from 'nprogress/nprogress';

import { VueResponsiveComponents } from 'vue-responsive-components';
import BootstrapVue from 'bootstrap-vue';
import Toasted from 'vue-toasted';
import Vue2TouchEvents from 'vue2-touch-events';
import VueLazyload from 'vue-lazyload';
import VCalendar from 'v-calendar';
import VueGtm from 'vue-gtm';
import Transitions from 'vue2-transitions';
import VueClipboard from 'vue-clipboard2';

import store from '@state/store';
import routes from './routes';

Bugsnag.start({
  apiKey: 'd9eafdf721af4b55b7cbe7ecd50f29c5',
  plugins: [new BugsnagPluginVue()],
});

Bugsnag.getPlugin('vue').installVueErrorHandler(Vue);

Vue.use(VueRouter);
Vue.use(BootstrapVue);
Vue.use(VueResponsiveComponents);
Vue.use(Vue2TouchEvents);
Vue.use(VCalendar);
Vue.use(Transitions);
Vue.use(VueClipboard);

Vue.use(VueMeta, {
  keyName: 'page', // The component option name that vue-meta looks for meta info on.
});

Vue.use(VueGtm, {
  id: 'GTM-TNXVPWH',
  debug: false,
  loadScript: true,
  vueRouter: router,
});

// Vue Toast docs: https://github.com/shakee93/vue-toasted#readme
Vue.use(Toasted, {
  position: 'top-center',
  duration: 5000,
  type: 'info',
  iconPack: 'fontawesome',
  icon: {
    name: 'check',
    after: false,
  },
  action: [
    {
      text: 'Close',
      onClick: (e, toastObject) => {
        toastObject.goAway(0);
      },
    },
  ],
  theme: 'outline',
});

Vue.use(VueLazyload, {
  preLoad: 1.3,
  // error: 'dist/error.png',
  // loading: 'https://media.giphy.com/media/3o7aD5lNmqChBB5yE0/giphy.gif',
  attempt: 1,
});

const router = new VueRouter({
  routes,
  // Use the HTML5 history API (i.e. normal-looking routes)
  // instead of routes with hashes (e.g. example.com/#/about).
  // This may require some server configuration in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  mode: 'history',
  // Simulate native-like scroll behavior when navigating to a new
  // route and using back/forward buttons.
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { x: 0, y: 0 };
    }
  },
});

// Before each route evaluates...
router.beforeEach((routeTo, routeFrom, next) => {
  // If this isn't an initial page load...
  if (routeFrom.name !== null) {
    // Start the route progress bar.
    NProgress.start();
  }

  // Check if auth is required on this route
  // (including nested routes).
  const authRequired = routeTo.matched.some((route) => route.meta.authRequired);

  // If auth isn't required for the route, just continue.
  if (!authRequired) return next();

  // If auth is required and the user is logged in...
  if (routeTo.query.authToken) {
    return store.dispatch('auth/autoLogin', routeTo.query.authToken).then((res) => {
      // Then continue if the token still represents a valid user,
      // otherwise reload to try again.
      // validUser ? next() : window.location.reload();

      handleAutoLoginRes(res);
    });
  } else if (store.getters['auth/loggedIn']) {
    // Validate the local user token...
    return store.dispatch('auth/validate').then((validUser) => {
      // Then continue if the token still represents a valid user,
      // otherwise redirect to login.
      validUser ? next() : redirectToLogin();
    });
  } else if ((routeTo.query && routeTo.query._branch_match_id) || routeTo.query.deepLink) {
    next({ name: 'public', query: routeTo.query });
  } else {
    // If auth is required and the user is NOT currently logged in,
    // redirect to login.
    redirectToLogin();
  }

  function handleAutoLoginRes(res) {
    if (res && res.status) {
      switch (res.status) {
        case 401:
          next({ name: '401' });
          break;
        case 500:
          let refreshCount = JSON.parse(window.localStorage.getItem('refreshCount'));

          if (!refreshCount || refreshCount <= 2) {
            refreshCount >= 1 ? window.localStorage.setItem('refreshCount', (refreshCount += 1)) : window.localStorage.setItem('refreshCount', 1);
            window.location.reload();
          } else {
            next({ name: '401' });
            window.localStorage.removeItem('refreshCount');
          }
          break;
      }
    } else {
      next();
    }
  }

  function redirectToLogin() {
    // Pass the original route to the login component
    next({ name: 'login', query: { redirectFrom: routeTo.fullPath } });
  }
});

router.beforeResolve(async (routeTo, routeFrom, next) => {
  // Create a `beforeResolve` hook, which fires whenever
  // `beforeRouteEnter` and `beforeRouteUpdate` would. This
  // allows us to ensure data is fetched even when params change,
  // but the resolved route does not. We put it in `meta` to
  // indicate that it's a hook we created, rather than part of
  // Vue Router (yet?).
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        // If a `beforeResolve` hook is defined, call it with
        // the same arguments as the `beforeEnter` hook.
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            // If the user chose to redirect...
            if (args.length) {
              // If redirecting to the same route we're coming from...
              if (routeFrom.name === args[0].name) {
                // Complete the animation of the route progress bar.
                NProgress.done();
              }
              // Complete the redirect.
              next(...args);
              reject(new Error('Redirected'));
            } else {
              resolve();
            }
          });
        } else {
          // Otherwise, continue resolving the route.
          resolve();
        }
      });
    }
    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    return;
  }

  // If we reach this point, continue resolving the route.
  next();
});

// When each route is finished evaluating...
router.afterEach((routeTo, routeFrom) => {
  // Complete the animation of the route progress bar.
  NProgress.done();
});

export default router;
